import type { IFindJobsFilter } from '../../../types/findJobsType'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import Breadcrumb from '../../Common/BreadCrumb/BreadCrumb'
import useFindJobsList from '../useFindJobsList'
import FilterBox from '../SideBar/FilterBox/FilterBox'
import JoinUs from '../SideBar/JoinUs/JoinUs'
import Content from '../Content/Content'
import TablePagination from '../../Notifications/TablePagination'
import JoinUsModal from './JoinUsModal'
import { buildQueryParams, parseURL } from '../helper'
import Link from 'next/link'
import CopyrightFooter from '../../Common/CopyrightFooter/CopyrightFooter'

type Props = {
  isPublic?: boolean
}

const FindJobsWrapper: React.FC<Props> = ({ isPublic = false }) => {
  const router = useRouter()
  const { id: resumeID } = router.query

  const [filter, setFilter] = useState<IFindJobsFilter>({
    search: '',
    location: '',
    category: undefined,
    jobType: [],
    workplace: [],
    career_level: [],
    date_posted: undefined,
    page: 1,
    resume_id: '',
  })
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data, isFetching } = useFindJobsList(filter, isPublic)

  useEffect(() => {
    const data = parseURL()
    setFilter(prev => ({
      ...filter,
      ...data,
      resume_id: resumeID as string,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query])

  // Whenever the filter changes, we want to change url
  useEffect(() => {
    const queryParams = buildQueryParams(filter)
    // Get current url
    const currentUrl = window.location.href
    // Remove the query params
    const url = currentUrl.split('?')[0]
    // Add the new query params
    const newUrl = url + (queryParams ? `?${queryParams}` : '')
    // Replace the url
    window.history.replaceState({}, '', newUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <>
      <Box
        sx={{
          mt: '130px',
        }}
      >
        <Breadcrumb title={'Find Jobs'} meta={'Find Jobs'} />
        <Grid container columnGap={6.25} rowGap={3}>
          <Grid xs={12} maxWidth={{ xs: '100%', md: '390px' }}>
            <Stack spacing={3.75}>
              <FilterBox filter={filter} setFilter={setFilter} />
              {isPublic && <JoinUs />}
            </Stack>
          </Grid>
          <Grid xs={12} md>
            <Content
              isFetching={isFetching}
              items={data?.data.results}
              setIsModalOpen={setIsModalOpen}
            />
            <Stack
              justifyContent={'center'}
              alignItems={'center'}
              mt={7.5}
              spacing={1.5}
            >
              {data?.data && data.data?.results?.length > 0 && (
                <TablePagination
                  count={Math.ceil(data.data.count / 10) || 1}
                  page={filter.page!}
                  disabled={isPublic}
                  onChange={(e, page) => {
                    if (isPublic) {
                      setIsModalOpen(true)
                      return
                    } else setFilter({ ...filter, page })
                  }}
                />
              )}
              {data?.data && data.data?.results?.length > 0 && (
                <Typography
                  variant="body_m_regular"
                  color={isPublic ? 'darkGray.disabled' : 'darkGray.main'}
                >
                  {filter.page! * data.data.results.length} of{' '}
                  {data?.data.count} items
                </Typography>
              )}
              {isPublic && (
                <Link href="/auth">
                  <Typography
                    variant="body_m_medium"
                    color={'mediumGreen.disabled'}
                    sx={{
                      textDecoration: 'underline',
                    }}
                  >
                    Login to See More
                  </Typography>
                </Link>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <JoinUsModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
        }}
      />
      <CopyrightFooter />
    </>
  )
}

export default FindJobsWrapper
